/**
 * Shared types and helpers for country threshold override UI.
 */

import type { TagCompareTo } from '@trafficpulse/shared';

import type { Country } from '@/hooks/smswiser/core/useSMSWiser';
import type { TagRuleCountryThreshold } from '@/hooks';

export const MAX_COUNTRY_THRESHOLD_ROWS = 50;

export const COUNTRY_THRESHOLD_HELP =
  'The countries listed here are compared against their own value instead of the default value above.';

export interface CountryThresholdUIGroup {
  id: string;
  countryKeys: string[];
  value: number;
  /** When set, this row COMPARES instead of pinning `value`. */
  compareTo?: TagCompareTo;
}

/** Rows merge into one UI group only when the whole threshold matches —
 *  the number AND the comparison (or its absence). */
export const sameThreshold = (
  a: { value: number; compareTo?: TagCompareTo },
  b: { value: number; compareTo?: TagCompareTo },
): boolean =>
  a.value === b.value &&
  JSON.stringify(a.compareTo ?? null) === JSON.stringify(b.compareTo ?? null);

/**
 * Build UI groups with stable ids (vg-0, vg-1, …) from consecutive runs of the same
 * numeric value in flatten order. Editing a threshold no longer changes React keys.
 */
export function rowsToUIGroups(rows: TagRuleCountryThreshold[]): CountryThresholdUIGroup[] {
  const filled = rows.filter((r) => r.countryKey.trim());
  const blocks: CountryThresholdUIGroup[] = [];
  let seq = 0;
  for (const r of filled) {
    const k = r.countryKey.trim().toLowerCase();
    const prev = blocks[blocks.length - 1];
    if (prev && sameThreshold(prev, r)) {
      if (!prev.countryKeys.includes(k)) prev.countryKeys.push(k);
    } else {
      blocks.push({
        id: `vg-${seq++}`,
        countryKeys: [k],
        value: r.value,
        ...(r.compareTo ? { compareTo: r.compareTo } : {}),
      });
    }
  }
  for (const b of blocks) {
    b.countryKeys.sort();
  }
  const drafts: CountryThresholdUIGroup[] = [];
  rows.forEach((r, idx) => {
    if (!r.countryKey.trim()) {
      drafts.push({
        id: `draft-${idx}`,
        countryKeys: [],
        value: r.value,
        ...(r.compareTo ? { compareTo: r.compareTo } : {}),
      });
    }
  });
  return [...blocks, ...drafts];
}

export function flattenUIGroups(groups: CountryThresholdUIGroup[]): TagRuleCountryThreshold[] {
  const out: TagRuleCountryThreshold[] = [];
  for (const g of groups) {
    const extra = g.compareTo ? { compareTo: g.compareTo } : {};
    if (g.countryKeys.length === 0) {
      out.push({ countryKey: '', value: g.value, ...extra });
    } else {
      for (const ck of g.countryKeys) {
        out.push({ countryKey: ck, value: g.value, ...extra });
      }
    }
  }
  return out;
}

export function countAssignedCountries(rows: TagRuleCountryThreshold[]): number {
  return rows.filter((r) => r.countryKey.trim()).length;
}

export function countriesAvailableForGroup(
  all: Country[],
  groups: CountryThresholdUIGroup[],
  groupId: string,
): Country[] {
  const current = groups.find((g) => g.id === groupId);
  const currentKeys = new Set(current?.countryKeys.map((k) => k.trim().toLowerCase()) ?? []);
  const taken = new Set<string>();
  for (const g of groups) {
    if (g.id === groupId) continue;
    for (const k of g.countryKeys) taken.add(k.trim().toLowerCase());
  }
  return all.filter((c) => {
    const k = c.key.trim().toLowerCase();
    return !taken.has(k) || currentKeys.has(k);
  });
}

export function othersCountryCount(groups: CountryThresholdUIGroup[], groupId: string): number {
  return groups
    .filter((g) => g.id !== groupId)
    .reduce((n, g) => n + g.countryKeys.length, 0);
}

export function CountBadge({ n }: { n: number }) {
  if (n <= 0) return null;
  return (
    <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary/12 px-1.5 text-[11px] font-semibold tabular-nums text-primary">
      {n}
    </span>
  );
}

export function CountryThresholdTableHead() {
  return (
    <>
      {/* Spelled out once, so "Value" in the column head needs no guessing. */}
      <p className="px-4 pt-2 text-[11px] leading-snug text-muted-foreground">
        {COUNTRY_THRESHOLD_HELP}
      </p>
      <div className="hidden border-b border-border/40 bg-muted/20 sm:grid sm:grid-cols-[minmax(0,1fr)_5.5rem_2.5rem] sm:gap-3 sm:px-4 sm:py-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Countries
        </span>
        <span className="text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Value
        </span>
        <span className="sr-only">Actions</span>
      </div>
    </>
  );
}
